/**
 * An admin's read-only view of one organization's desk (phase 3d §7).
 *
 * "Open their desk (read-only)" on the admin organization page goes to
 * `/books/view/<id>` (pages/AdminViewPage.js), which asks the admin callable
 * `openOrganizationAsAdmin` to log the look and hand the organization back,
 * keeps it HERE and opens the ordinary desk. Every desk page then runs on
 * that organization as `viewer` — the shell shows the banner, the session's
 * client sends `x-books-admin-view` — until Exit ends it. The desk's routes
 * and links are the member's own; only this kept view differs.
 *
 * What is kept is a REQUEST, not a grant: `entitlementsFor` honours it only
 * for the admin who opened it (the `admin` claim, the one the database
 * checks, and the same uid). sessionStorage can be written by hand, so it
 * is not what keeps the look honest — the DATABASE is: it answers `viewer`
 * only to the admin claim AND only while the callable has logged a look at
 * that organization for that admin in the last 12 hours (`member_role` →
 * `admin_look_logged`). A view written here by hand, or kept past its 12
 * hours, reads nothing; the log cannot be skipped.
 *
 * Kept for the page load in the module and for this browser TAB in
 * sessionStorage (so a reload stays in the view, another tab opens the
 * admin's own desk). Storage that fails keeps the view until a reload. It
 * lapses when the database's look does — 12 hours after it was opened — so
 * a tab left open does not show the organization with nothing in it.
 */
const KEY = 'books.adminView';

// how long one opening lasts: admin_look_logged's window
// (supabase/migrations/20260926000300_desk_gaps.sql)
export const ADMIN_LOOK_MS = 12 * 60 * 60 * 1000;

const storage = () => {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
};

const isView = (v) =>
  Boolean(v) &&
  typeof v.uid === 'string' &&
  v.uid !== '' &&
  typeof v.openingId === 'string' &&
  typeof v.organization?.id === 'string' &&
  v.organization.id !== '' &&
  typeof v.organization.name === 'string' &&
  Number.isFinite(v.openedAt);

const readStored = () => {
  try {
    const parsed = JSON.parse(storage()?.getItem(KEY) ?? 'null');
    return isView(parsed) ? parsed : null;
  } catch {
    return null;
  }
};

// undefined until first read this page load; then the view or null, the
// SAME object until it is started or ended (hooks depend on it)
let current;
let openings = 0;

/** The kept view `{ uid, organization, openingId, openedAt }`, or null. */
export function readAdminView() {
  if (current === undefined) current = readStored();
  if (current && Date.now() - current.openedAt >= ADMIN_LOOK_MS) endAdminView();
  return current;
}

/**
 * Keep the view the admin callable opened: `organization` as it returned it
 * (the columns the desk's session holds). Each opening is its own view, so
 * a session kept from an earlier one is never reused for it.
 */
export function startAdminView({ uid, organization }) {
  openings += 1;
  const openedAt = Date.now();
  const view = {
    uid,
    organization,
    openingId: `${openedAt.toString(36)}-${openings}`,
    openedAt,
  };
  if (!isView(view)) {
    throw new Error(
      'startAdminView: a view needs the admin and the named organization'
    );
  }
  current = view;
  try {
    storage()?.setItem(KEY, JSON.stringify(view));
  } catch {
    // kept for this page load only
  }
  return view;
}

/** Exit: the desk is the admin's own again. */
export function endAdminView() {
  current = null;
  try {
    storage()?.removeItem(KEY);
  } catch {
    // nothing kept to remove
  }
}

/**
 * The account signed in is now `uid` (null: nobody). A view kept for anyone
 * else ends — so a sign-out anywhere (the design app's menu, another tab's)
 * ends it, not only the desk's Sign out, and the next sign-in never walks
 * back into it. The app calls this on every change of the signed-in account
 * (App.js); the same admin again (a reload, a token refresh) keeps it.
 */
export function followSignIn(uid) {
  const view = readAdminView();
  if (view && view.uid !== uid) endAdminView();
}

/** Where an admin opens a view of `organizationId`, and where Exit goes. */
export const adminViewPath = (organizationId) =>
  `/books/view/${organizationId}`;
export const adminOrganizationPath = (organizationId) =>
  `/admin/organizations/${organizationId}`;
